import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Button, IconCrossThick, IconColorBit } from '@sololearnorg/sol';
import classNames from 'classnames';
import s from './SlInfoScreen.scss';
import { SlTransition } from '../SlTransition/SlTransition';
import { useFadeOut } from '../SlHooks/useFadeOut';
import { SlAnimationLoader } from '../SlAnimationLoader/SlAnimationLoader';
import { i18n } from '../../../symphony';
import { useNumberCounter } from '../SlHooks/useNumberCounter';

interface SlInfoScreenProps {
	showCloseIcon?: boolean;
	onCloseClick?: () => void;
	animationData?: any;
	icon?: any;
	title?: string;
	subTitle?: string;
	reward: {
		showReward: boolean;
		showRewardTitle?: boolean;
		xps?: number;
		bits?: number;
		shouldAnimate?: boolean;
	};
	buttonTitle: string;
	onButtonClick?: () => void;
}

export const SlInfoScreen = ({
	showCloseIcon = false,
	onCloseClick,
	animationData,
	title,
	subTitle,
	reward,
	buttonTitle,
	icon,
	onButtonClick,
}: SlInfoScreenProps) => {
	useStyles(s);

	const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

	const handleContinueClick = () => {
		startFadeOut(() => {
			onButtonClick();
		});
	};

	const animatedXps = useNumberCounter(reward.xps, 1500) || 0;
	const animatedBits = useNumberCounter(reward.bits, 1500) || 0;

	return (
		<div className="sl-info-screen">
			{showCloseIcon && <IconCrossThick className="sl-info-screen__close-icon" size={24} onClick={onCloseClick} />}
			<div className="sl-info-screen__content">
				<div>
					<SlTransition
						fadeIn={{ delay: 0.1, duration: 0.3 }}
						fadeOut={{ delay: 0, duration: 0.3 }}
						isFadeOutStarted={isFadeOutStarted}
					>
						<SlAnimationLoader
							show
							autoplay
							animationData={animationData}
							loop={false}
						/>
						{icon}
					</SlTransition>
				</div>
				<SlTransition
					fadeIn={{ delay: 0.3, duration: 0.3 }}
					fadeOut={{ delay: 0.1, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<div>
						<h2 className="sl-info-screen__content__title">{title}</h2>
						<p className="sl-info-screen__content__subtitle">{subTitle}</p>
					</div>
				</SlTransition>
				<SlTransition
					fadeIn={{ delay: 0.5, duration: 0.3 }}
					fadeOut={{ delay: 0.2, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<div className={classNames('sl-info-screen__content__reward', {
						show: reward.showReward,
					})}
					>
						{reward?.showRewardTitle && (
							<span className="sl-info-screen__content__reward__title">
								{i18n.t('reward.your_reward')}
							</span>
						)}
						<span>
							+
							{reward?.shouldAnimate ? animatedXps : reward?.xps}
							{' '}
							XP
						</span>
						<span className="sl-info-screen__content__reward__bits">
							+
							{reward?.shouldAnimate ? animatedBits : reward?.bits}
							{' '}
							<IconColorBit />
						</span>
					</div>
				</SlTransition>
			</div>
			<div className="sl-info-screen__footer">
				<SlTransition
					fadeIn={{ delay: 0.7, duration: 0.3 }}
					fadeOut={{ delay: 0.3, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<Button
						onClick={handleContinueClick}
						buttonType="primary"
						className="sl-info-screen__footer__btn"
					>
						{buttonTitle}
					</Button>
				</SlTransition>
			</div>
		</div>
	);
};
